import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { ScrollToTopButton, Container, Stack, PageHeader, ProgressSpinner } from '@/components/ui';
import { MainLayout } from '@/layouts/MainLayout';
import { FixturesList } from '@/components/fixtures/FixturesList';
import { FixtureSkeleton } from '@/components/fixtures/FixtureSkeleton';
import { useFixturesStore } from '@/store/fixturesStore';

export default function FixturesPage() {
  const {
    fixtures,
    groupedFixtures,
    loading,
    error,
    refreshing,
    selectedFixture,
    fetchFixtures,
    refreshFixtures,
    setSelectedFixture,
    setSelectedFixtureById,
    clearError,
  } = useFixturesStore();
  const location = useLocation();

  const selectedFixtureId = selectedFixture?.fixtureId ?? null;

  useEffect(() => {
    fetchFixtures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const restoredFixtureId = (location.state as { fixtureId?: number } | null)?.fixtureId;

    if (!restoredFixtureId || fixtures.length === 0) {
      return;
    }

    setSelectedFixtureById(restoredFixtureId);

    setTimeout(() => {
      const target = document.getElementById(`fixture-${restoredFixtureId}`);
      if (!target) {
        return;
      }

      target.scrollIntoView({ block: 'start' });
      window.scrollBy(0, -107);
    }, 100);
  }, [fixtures, location.state, setSelectedFixtureById]);

  const handleRefresh = () => {
    clearError();
    refreshFixtures();
  };

  const handleToggleSelected = (fixtureId: number) => {
    if (selectedFixtureId === fixtureId) {
      setSelectedFixture(null);
      return;
    }

    const fixture = fixtures.find((item) => item.fixtureId === fixtureId) ?? null;
    setSelectedFixture(fixture);
  };

  const title = fixtures.length > 0 ? `Fixtures (${fixtures.length})` : 'Fixtures';

  return (
    <MainLayout>
      <Container className="pb-8">
        <Stack direction="vertical" spacing={6} className="fixtures-page">
          <PageHeader
            title={title}
            actionsInline
            actions={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                title="Refresh"
                aria-label="Refresh"
                className="!min-h-9 !min-w-9 shrink-0 !px-2 !py-2"
              >
                {refreshing ? <ProgressSpinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            }
          />

          {error && (
            <Alert variant="error" className="flex items-center justify-between">
              <div>
                <p className="font-medium">Error loading fixtures</p>
                <p className="text-sm opacity-90">{error}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={clearError}>
                  Dismiss
                </Button>
                <Button size="sm" onClick={handleRefresh}>
                  Try Again
                </Button>
              </div>
            </Alert>
          )}

          {loading && fixtures.length === 0 ? (
            <FixtureSkeleton />
          ) : fixtures.length === 0 && !loading ? (
            <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
              <div className="py-8 text-center">
                <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-gray-100 p-3 dark:bg-gray-700">
                  <Search className="h-6 w-6 text-gray-400" />
                </div>
                <h3 className="mb-2 text-base font-medium text-gray-900 dark:text-white">
                  No fixtures found
                </h3>
                <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                  No fixtures are available at the moment. Try refreshing to load the latest data.
                </p>
                <Button onClick={handleRefresh} disabled={refreshing}>
                  {refreshing ? 'Refreshing...' : 'Refresh Data'}
                </Button>
              </div>
            </div>
          ) : (
            <FixturesList
              groupedFixtures={groupedFixtures}
              loading={loading}
              selectedFixtureId={selectedFixtureId}
              onToggleSelected={handleToggleSelected}
            />
          )}

          <ScrollToTopButton />
        </Stack>
      </Container>
    </MainLayout>
  );
}
